import React, { useContext } from "react";
import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { RefModel } from "../../../../models/base/ref-model";
import { EmployeeRef } from "../../../../models/employees";
import { TnAOverviewsFilter } from "../../../../models/tna-overviews/tna-overview.filter";
import { TnAWorkedTimeTotalsDTO } from "../../../../models/tna-worked-time-totals";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { TnAWorkedTimeTotalsApiService } from "../../../../services/tna-worked-time-totals/tna-worked-time-totals-api.service";
import { FT_MTPAL } from "../../../../utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "../../../../utils/time-utils";
import Table from "../../../../reusable-components/Table/Table";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { EmployeeNameContainer } from "../styles";

interface Props {
  navigateToTimesheet: (employee: EmployeeRef | null) => void;
  quickfilters: (items: RefModel[], loading: boolean) => JSX.Element;
  getAllItems: ((items: RefModel[]) => void) | undefined;
}

const WorkedTimeTotalsView = ({ navigateToTimesheet, quickfilters, getAllItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_worked_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const theme = useTheme();

  const {
    legalEntity: { legalEntityOption },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    navigateToTimesheet(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t(`common_words.employee`)} | ${t(`common_words.legal_entity`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => <Typography>{`${t(`common_words.tna_employee_group`)}`}</Typography>,
      headerAlign: "center",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "dueHours",
      renderCell: ({ row }) => {
        return `${!!row?.dueHours ? row?.dueHours.toFixed(2) : ""}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.due_hours`)}`}</Typography>,
      headerAlign: "center",
      align: "right",
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "workedTime",
      renderCell: ({ row }) => {
        return `${!!row?.workedTime ? row?.workedTime.toFixed(2) : ""}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.worked_hours`)}`}</Typography>,
      headerAlign: "center",
      align: "right",
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "vacationHours",
      renderCell: ({ row }) => {
        return `${!!row?.vacationHours ? row?.vacationHours.toFixed(2) : ""}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.vacation_hours`)}`}</Typography>,
      headerAlign: "center",
      align: "right",
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "paidLeaveHours",
      renderCell: ({ row }) => {
        return `${!!row?.paidLeaveHours ? row?.paidLeaveHours.toFixed(2) : ""}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.paid_leave_hours`)}`}</Typography>,
      headerAlign: "center",
      align: "right",
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: "businessTripHours",
      renderCell: ({ row }) => {
        return `${!!row?.businessTripHours ? row?.businessTripHours.toFixed(2) : ""}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.business_trip_hours`)}`}</Typography>,
      headerAlign: "center",
      align: "right",
      flex: 1,
      disableColumnMenu: true,
    },
  ];

  const defaultItem: Partial<TnAWorkedTimeTotalsDTO> = {
    employee: null,
    dueHours: 0,
    workedTime: 0,
    vacationHours: 0,
    paidLeaveHours: 0,
    businessTripHours: 0,
  };

  return (
    <Table
      displayTitle={false}
      translationRootPath={tRP}
      columns={columns}
      serviceApi={new TnAWorkedTimeTotalsApiService()}
      propsFilter={
        {
          ...legalEntityOption,
          ...employeeGroupOption,
          ...onlyCurrentEmployeesOptionValue,
          year,
          month: month + 1,
        } as TnAOverviewsFilter
      }
      defaultItem={defaultItem}
      filter={FT_MTPAL}
      readOnly
      quickFilters={quickfilters}
      getAllItems={getAllItems}
    />
  );
};

export default WorkedTimeTotalsView;
